import { useEffect, useState, Fragment } from 'react';
import { API_URL } from '../config';
import { Building2, Plus, Edit2, Trash2, X, MapPin, Package, CreditCard, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTableSort } from '../hooks/useTableSort';
import SortableTh from '../components/SortableTh';

/** Misma política que `Layout.jsx` para usuarios legacy sin lista de permisos. */
function hasPermission(user, permission) {
    if (!user) return false;
    if (!user.permissions || !Array.isArray(user.permissions)) return true;
    return user.permissions.includes(permission);
}

export default function SucursalesPage() {
    const { user } = useAuth();
    const [sucursales, setSucursales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        direccion: '',
        comuna: '',
        estado: true,
        timezone: 'America/Santiago',
        shift_cutoff_time: '04:00',
    });
    const [detailOpenId, setDetailOpenId] = useState(null);
    const [detailBodegas, setDetailBodegas] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [allCajas, setAllCajas] = useState([]);
    const [newCajaNombre, setNewCajaNombre] = useState('');
    const [newBodegaNombre, setNewBodegaNombre] = useState('');
    const canViewCajas = hasPermission(user, 'cajas_ver');
    const canEditCajas = hasPermission(user, 'cajas_editar');
    const canViewBodegas = hasPermission(user, 'bodegas_ver');
    const canEditBodegas = hasPermission(user, 'bodegas_editar');

    useEffect(() => {
        fetchSucursales();
    }, []);

    useEffect(() => {
        const load = async () => {
            if (!canViewCajas) return;
            try {
                const res = await fetch(`${API_URL}/api/cajas`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                if (!res.ok) return;
                const data = await res.json();
                setAllCajas(Array.isArray(data) ? data : []);
            } catch {
                /* ignore */
            }
        };
        load();
    }, [canViewCajas, user?.id]);

    const authHeaders = () => ({
        'Authorization': `Bearer ${localStorage.getItem('token')}`
    });

    const fetchSucursales = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/sucursales/stats`, { headers: authHeaders() });
            const data = await res.json();
            setSucursales(data);
        } catch (err) {
            console.error('Error fetching sucursales:', err);
        } finally {
            setLoading(false);
        }
    };

    const openModal = (sucursal = null) => {
        if (sucursal) {
            setEditingId(sucursal.id);
            setFormData({
                nombre: sucursal.nombre || '',
                direccion: sucursal.direccion || '',
                comuna: sucursal.comuna || '',
                estado: sucursal.estado !== false,
                timezone: sucursal.timezone || 'America/Santiago',
                shift_cutoff_time: (sucursal.shift_cutoff_time || '04:00:00').slice(0, 5),
            });
        } else {
            setEditingId(null);
            setFormData({
                nombre: '',
                direccion: '',
                comuna: '',
                estado: true,
                timezone: 'America/Santiago',
                shift_cutoff_time: '04:00',
            });
        }
        setShowModal(true);
    };

    const fetchBodegasFor = async (sucursalId) => {
        if (!canViewBodegas) return [];
        try {
            const res = await fetch(`${API_URL}/api/bodegas/sucursal/${sucursalId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (!res.ok) return [];
            return await res.json();
        } catch {
            return [];
        }
    };

    const toggleDetail = async (sucId) => {
        if (detailOpenId === sucId) {
            setDetailOpenId(null);
            setDetailBodegas([]);
            return;
        }
        setDetailOpenId(sucId);
        setDetailLoading(true);
        const bods = await fetchBodegasFor(sucId);
        setDetailBodegas(Array.isArray(bods) ? bods : []);
        setDetailLoading(false);
    };

    const refreshCajasList = async () => {
        if (!canViewCajas) return;
        try {
            const res = await fetch(`${API_URL}/api/cajas`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (!res.ok) return;
            const data = await res.json();
            setAllCajas(Array.isArray(data) ? data : []);
        } catch {
            /* ignore */
        }
    };

    const putCaja = async (cajaId, payload) => {
        const res = await fetch(`${API_URL}/api/cajas/${cajaId}`, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            alert(err.error || 'Error al actualizar la caja');
            return false;
        }
        await refreshCajasList();
        return true;
    };

    const toggleCajaOperativa = (caja) =>
        putCaja(caja.id, {
            nombre: caja.nombre,
            sucursal_id: caja.sucursal_id,
            tipo: caja.tipo,
            estado: !(caja.estado === true)
        });

    const saveCajaNombre = async (caja, nombre) => {
        const trimmed = String(nombre || '').trim();
        if (!trimmed) {
            alert('El nombre es obligatorio');
            return;
        }
        await putCaja(caja.id, {
            nombre: trimmed,
            sucursal_id: caja.sucursal_id,
            tipo: caja.tipo,
            estado: caja.estado === true
        });
    };

    const createCajaInSucursal = async (sucursalId) => {
        if (!canEditCajas) return;
        const nombre = String(newCajaNombre || '').trim();
        if (!nombre) {
            alert('Ingrese un nombre para la caja');
            return;
        }
        const res = await fetch(`${API_URL}/api/cajas`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ nombre, sucursal_id: sucursalId })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            alert(err.error || 'No se pudo crear la caja');
            return;
        }
        setNewCajaNombre('');
        await refreshCajasList();
        fetchSucursales();
    };

    const createBodegaInSucursal = async (sucursalId) => {
        if (!canEditBodegas) return;
        const nombre = String(newBodegaNombre || '').trim();
        if (!nombre) {
            alert('Ingrese un nombre para la bodega');
            return;
        }
        const res = await fetch(`${API_URL}/api/bodegas`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ nombre, sucursal_id: sucursalId })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            alert(err.error || 'No se pudo crear la bodega');
            return;
        }
        setNewBodegaNombre('');
        const bods = await fetchBodegasFor(sucursalId);
        setDetailBodegas(Array.isArray(bods) ? bods : []);
        fetchSucursales();
    };

    const updateBodegaNombre = async (bodegaId, nombre, sucursalIdRef) => {
        const trimmed = String(nombre || '').trim();
        if (!trimmed) {
            alert('El nombre es obligatorio');
            return;
        }
        const res = await fetch(`${API_URL}/api/bodegas/${bodegaId}`, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ nombre: trimmed, sucursal_id: sucursalIdRef })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            alert(err.error || 'Error al guardar');
            return;
        }
        const bods = await fetchBodegasFor(sucursalIdRef);
        setDetailBodegas(Array.isArray(bods) ? bods : []);
    };

    const deleteBodega = async (bodegaId, sucursalIdRef) => {
        if (!confirm('¿Eliminar esta bodega?')) return;
        const res = await fetch(`${API_URL}/api/bodegas/${bodegaId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            alert(err.error || 'No se puede eliminar');
            return;
        }
        const bods = await fetchBodegasFor(sucursalIdRef);
        setDetailBodegas(Array.isArray(bods) ? bods : []);
        fetchSucursales();
    };

    const cajasDeSucursal = (sucursalId) =>
        allCajas.filter(c => Number(c.sucursal_id) === Number(sucursalId));

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingId 
                ? `${API_URL}/api/sucursales/${editingId}`
                : `${API_URL}/api/sucursales`;
            
            const payload = editingId
                ? {
                    ...formData,
                    estado: formData.estado === true,
                    shift_cutoff_time: formData.shift_cutoff_time || '04:00',
                }
                : { nombre: formData.nombre, direccion: formData.direccion, comuna: formData.comuna };

            const res = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                setShowModal(false);
                fetchSucursales();
            } else {
                const error = await res.json();
                alert(error.error || 'Error al guardar');
            }
        } catch (err) {
            console.error('Error saving sucursal:', err);
            alert('Error al guardar la sucursal');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar esta sucursal?')) return;
        
        try {
            const res = await fetch(`${API_URL}/api/sucursales/${id}`, { method: 'DELETE', headers: authHeaders() });
            if (res.ok) {
                fetchSucursales();
            } else {
                const error = await res.json();
                alert(error.error || 'Error al eliminar');
            }
        } catch (err) {
            console.error('Error deleting sucursal:', err);
            alert('Error al eliminar la sucursal');
        }
    };

    const { sorted: sortedSucursales, thProps } = useTableSort(sucursales, {
        getters: {
            cajas_count: (s) => parseInt(s.cajas_count, 10) || 0,
            bodegas_count: (s) => parseInt(s.bodegas_count, 10) || 0,
            estado: (s) => (s.estado !== false ? 1 : 0),
        },
    });

    return (
        <div className="page sucursales-page p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Building2 className="text-indigo-600" size={28} />
                        Gestión de Sucursales
                    </h1>
                    <p className="text-gray-500 mt-1">
                        Administra sucursales; desde cada local puede ver y gestionar cajas y bodegas vinculadas (según sus permisos).
                    </p>
                </div>
                <button 
                    className="btn-primary flex items-center gap-2"
                    onClick={() => openModal()}
                >
                    <Plus size={18} /> Nueva Sucursal
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-indigo-100 rounded-lg">
                            <Building2 className="text-indigo-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Total Sucursales</p>
                            <p className="text-2xl font-bold">{sucursales.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-green-100 rounded-lg">
                            <CreditCard className="text-green-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Total Cajas</p>
                            <p className="text-2xl font-bold">
                                {sucursales.reduce((sum, s) => sum + (parseInt(s.cajas_count) || 0), 0)}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-orange-100 rounded-lg">
                            <Package className="text-orange-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Total Bodegas</p>
                            <p className="text-2xl font-bold">
                                {sucursales.reduce((sum, s) => sum + (parseInt(s.bodegas_count) || 0), 0)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Cargando...</div>
                ) : sucursales.length === 0 ? (
                    <div className="p-8 text-center">
                        <Building2 size={48} className="mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">No hay sucursales creadas</p>
                        <button 
                            onClick={() => openModal()}
                            className="mt-4 btn-primary"
                        >
                            Crear primera sucursal
                        </button>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="px-4 py-3 w-12" />
                                <SortableTh columnKey="id" {...thProps} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</SortableTh>
                                <SortableTh columnKey="nombre" {...thProps} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</SortableTh>
                                <SortableTh columnKey="direccion" {...thProps} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dirección</SortableTh>
                                <SortableTh columnKey="cajas_count" {...thProps} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" align="center">Cajas</SortableTh>
                                <SortableTh columnKey="bodegas_count" {...thProps} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" align="center">Bodegas</SortableTh>
                                <SortableTh columnKey="estado" {...thProps} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" align="center">Estado</SortableTh>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {sortedSucursales.map(suc => (
                                <Fragment key={suc.id}>
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-4 py-4 text-center">
                                            <button
                                                type="button"
                                                title="Cajas y bodegas de este local"
                                                className="p-1 rounded text-gray-600 hover:bg-gray-100"
                                                onClick={() => toggleDetail(suc.id)}
                                                disabled={!(canViewCajas || canViewBodegas)}
                                            >
                                                {detailOpenId === suc.id ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">#{suc.id}</td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <Building2 size={18} className="text-indigo-500" />
                                            <span className="font-medium">{suc.nombre}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        <div className="flex items-center gap-1">
                                            <MapPin size={14} />
                                            {suc.direccion || '-'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                                            {suc.cajas_count || 0}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                                            {suc.bodegas_count || 0}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`px-2 py-1 rounded-full text-xs ${
                                            suc.estado !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                        }`}>
                                            {suc.estado !== false ? 'Activa' : 'Inactiva'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex justify-center gap-2">
                                            <button
                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                                onClick={() => openModal(suc)}
                                                title="Editar"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                            <button
                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                                onClick={() => handleDelete(suc.id)}
                                                title="Eliminar"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                {detailOpenId === suc.id && (
                                    <tr className="bg-slate-50">
                                        <td colSpan={8} className="px-6 py-4 text-sm text-gray-700">
                                            {!(canViewCajas || canViewBodegas) ? (
                                                <p>
                                                    No tiene permiso para ver cajas ni bodegas. Solicite los permisos de
                                                    ver cajas y/o ver bodegas en su rol.
                                                </p>
                                            ) : detailLoading ? (
                                                <p className="text-gray-500">Cargando bodegas…</p>
                                            ) : (
                                                <div className="grid md:grid-cols-2 gap-6">
                                                    {canViewCajas && (
                                                        <div>
                                                            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                                                <CreditCard size={18} /> Cajas
                                                            </h3>
                                                            <div className="space-y-2 mb-4">
                                                                {cajasDeSucursal(suc.id).length === 0 ? (
                                                                    <p className="text-gray-500 text-sm">No hay cajas en esta sucursal.</p>
                                                                ) : (
                                                                    cajasDeSucursal(suc.id).map(c => (
                                                                        <div
                                                                            key={c.id}
                                                                            className="flex flex-wrap items-center gap-2 bg-white border rounded-lg p-2"
                                                                        >
                                                                            <input
                                                                                defaultValue={c.nombre}
                                                                                className="flex-1 min-w-[140px] border rounded px-2 py-1 text-sm"
                                                                                disabled={!canEditCajas}
                                                                                onBlur={e => {
                                                                                    if (!canEditCajas) return;
                                                                                    if (e.target.value.trim() !== c.nombre)
                                                                                        saveCajaNombre(c, e.target.value);
                                                                                }}
                                                                            />
                                                                            <span className="text-xs text-gray-500">#{c.id}</span>
                                                                            <label className="flex items-center gap-1 text-xs">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={c.estado === true}
                                                                                    disabled={!canEditCajas}
                                                                                    onChange={() =>
                                                                                        canEditCajas && toggleCajaOperativa(c)
                                                                                    }
                                                                                />
                                                                                Operativa POS
                                                                            </label>
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </div>
                                                            {canEditCajas && (
                                                                <div className="flex gap-2">
                                                                    <input
                                                                        className="flex-1 border rounded px-2 py-1 text-sm"
                                                                        placeholder="Nueva caja…"
                                                                        value={newCajaNombre}
                                                                        onChange={e => setNewCajaNombre(e.target.value)}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                                                                        onClick={() => createCajaInSucursal(suc.id)}
                                                                    >
                                                                        Agregar
                                                                    </button>
                                                                </div>
                                                            )}
                                                            <p className="text-xs text-gray-500 mt-2">
                                                                Solo cajas y sucursales activas permiten vender/cobrar en el POS.
                                                            </p>
                                                        </div>
                                                    )}
                                                    {canViewBodegas && (
                                                        <div>
                                                            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                                                <Package size={18} /> Bodegas
                                                            </h3>
                                                            <ul className="space-y-2 mb-4">
                                                                {detailBodegas.map(b => (
                                                                    <li
                                                                        key={b.id}
                                                                        className="flex flex-wrap items-center gap-2 bg-white border rounded-lg p-2"
                                                                    >
                                                                        <input
                                                                            defaultValue={b.nombre}
                                                                            className="flex-1 min-w-[140px] border rounded px-2 py-1 text-sm"
                                                                            disabled={!canEditBodegas}
                                                                            onBlur={ev => {
                                                                                if (!canEditBodegas) return;
                                                                                if (
                                                                                    ev.target.value.trim() !== b.nombre
                                                                                )
                                                                                    updateBodegaNombre(b.id, ev.target.value, suc.id);
                                                                            }}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            disabled={!canEditBodegas}
                                                                            className="text-red-600 p-1 disabled:opacity-40"
                                                                            title="Eliminar"
                                                                            onClick={() =>
                                                                                deleteBodega(b.id, suc.id)
                                                                            }
                                                                        >
                                                                            <Trash2 size={16} />
                                                                        </button>
                                                                    </li>
                                                                ))}
                                                                {detailBodegas.length === 0 && (
                                                                    <li className="text-gray-500 text-sm">
                                                                        Sin bodegas en esta sucursal.
                                                                    </li>
                                                                )}
                                                            </ul>
                                                            {canEditBodegas && (
                                                                <div className="flex gap-2">
                                                                    <input
                                                                        className="flex-1 border rounded px-2 py-1 text-sm"
                                                                        placeholder="Nueva bodega…"
                                                                        value={newBodegaNombre}
                                                                        onChange={e =>
                                                                            setNewBodegaNombre(e.target.value)
                                                                        }
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="px-3 py-1 bg-indigo-500 text-gray-900 rounded text-sm hover:bg-indigo-700"
                                                                        onClick={() =>
                                                                            createBodegaInSucursal(suc.id)
                                                                        }
                                                                    >
                                                                        Agregar
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">
                                {editingId ? 'Editar Sucursal' : 'Nueva Sucursal'}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Nombre *</label>
                                <input
                                    className="w-full p-2 border rounded-lg"
                                    required
                                    value={formData.nombre}
                                    onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                                    placeholder="Ej. Sucursal Central"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Dirección</label>
                                <input
                                    className="w-full p-2 border rounded-lg"
                                    value={formData.direccion}
                                    onChange={e => setFormData({ ...formData, direccion: e.target.value })}
                                    placeholder="Ej. Av. Principal 123"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Comuna</label>
                                <input
                                    className="w-full p-2 border rounded-lg"
                                    value={formData.comuna}
                                    onChange={e => setFormData({ ...formData, comuna: e.target.value })}
                                    placeholder="Ej. Santiago"
                                />
                            </div>
                            {editingId && (
                                <>
                                <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                                    <p className="text-xs font-semibold text-slate-600 mb-2">Día operacional (cierre de caja)</p>
                                    <label className="block text-sm font-medium mb-1">Zona horaria</label>
                                    <input
                                        className="w-full p-2 border rounded-lg mb-2"
                                        value={formData.timezone}
                                        onChange={e => setFormData({ ...formData, timezone: e.target.value })}
                                        placeholder="America/Santiago"
                                    />
                                    <label className="block text-sm font-medium mb-1">Hora de corte</label>
                                    <input
                                        type="time"
                                        className="w-full p-2 border rounded-lg"
                                        value={formData.shift_cutoff_time}
                                        onChange={e => setFormData({ ...formData, shift_cutoff_time: e.target.value })}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Ventas y cierres antes de esta hora (madrugada) cuentan para el día operacional anterior.
                                    </p>
                                </div>
                                <div className="mb-4">
                                    <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.estado === true}
                                            onChange={e =>
                                                setFormData({ ...formData, estado: e.target.checked })
                                            }
                                        />
                                        Sucursal activa (necesaria para operar POS en este local)
                                    </label>
                                </div>
                                </>
                            )}
                            <div className="flex gap-3 mt-6">
                                <button 
                                    type="button" 
                                    onClick={() => setShowModal(false)} 
                                    className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    className="flex-1 py-2 bg-indigo-500 text-gray-900 rounded-lg hover:bg-indigo-700"
                                >
                                    {editingId ? 'Guardar Cambios' : 'Crear Sucursal'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
